/**
 * IndustryMappingBlocks — helper functions and sub-components for IndustryMappingPanel.
 * Extracted from IndustryMappingPanel.jsx to reduce file complexity.
 */
import React from 'react';
import { Globe } from 'lucide-react';

// ── snapshotJunction ──────────────────────────────────────────────────────────
export function snapshotJunction(j) {
  const out = {};
  for (const code of Object.keys(j || {})) {
    out[code] = {
      Module:    Array.from(j[code].Module).sort(),
      SubModule: Array.from(j[code].SubModule).sort(),
      Screen:    Array.from(j[code].Screen).sort()
    };
  }
  return out;
}

// ── Table style helpers ───────────────────────────────────────────────────────
export function stickyHeader(left, _w, z, bg = 'var(--surface-2)', color = 'var(--text-primary, #0f172a)') {
  return {
    position: 'sticky', top: 0, left, zIndex: z,
    background: bg, color, padding: '6px 10px',
    borderRight: '1px solid #e2e8f0', borderBottom: '2px solid #cbd5e1',
    fontSize: 12, fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap'
  };
}

export function industryColHeader() {
  return {
    position: 'sticky', top: 0, zIndex: 2,
    background: 'var(--surface-2)', color: 'var(--text-secondary, #475569)',
    padding: '6px 4px', height: 120,
    borderRight: '1px solid #f1f5f9', borderBottom: '2px solid #cbd5e1',
    fontSize: 12, fontWeight: 700, textAlign: 'center'
  };
}

export function stickyCell(left, _w, bg = 'var(--surface)', indent = 0) {
  return {
    position: 'sticky', left, zIndex: 1,
    background: bg, padding: `4px 10px 4px ${10 + indent}px`,
    borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  };
}

// ── ScopeBadge ────────────────────────────────────────────────────────────────
export const ScopeBadge = React.memo(function ScopeBadge({ scope, small }) {
  const map = {
    Common:   { bg: '#e0f2fe', fg: '#0369a1', label: 'COMMON'   },
    Cross:    { bg: '#ede9fe', fg: '#6d28d9', label: 'CROSS'    },
    Vertical: { bg: '#dcfce7', fg: '#15803d', label: 'VERTICAL' }
  };
  const m = map[scope] || map.Vertical;
  return (
    <span style={{
      marginLeft: 6, padding: small ? '0 5px' : '1px 6px', borderRadius: 4,
      background: m.bg, color: m.fg, fontSize: small ? 8 : 9, fontWeight: 700, letterSpacing: '0.06em'
    }}>{m.label}</span>
  );
});

// ── CommonCell ────────────────────────────────────────────────────────────────
export const CommonCell = React.memo(function CommonCell({ active, entityType, entityId, testId }) {
  return (
    <td
      data-cell="common"
      data-entity-type={entityType}
      data-entity-id={entityId}
      data-testid={testId}
      style={{
        textAlign: 'center', padding: 0, cursor: 'pointer',
        background: active ? 'rgba(14,165,233,0.15)' : 'var(--surface)',
        borderRight: '2px solid #cbd5e1', borderBottom: '1px solid #f1f5f9',
        position: 'sticky', left: 320, zIndex: 1
      }}
    >
      <div style={{
        width: 22, height: 22, margin: '6px auto', borderRadius: 4,
        background: active ? '#0ea5e9' : 'var(--surface)',
        border: `2px solid ${active ? '#0369a1' : '#cbd5e1'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {active && <Globe size={12} color="#fff" />}
      </div>
    </td>
  );
});

// ── IndustryCell ──────────────────────────────────────────────────────────────
export const IndustryCell = React.memo(function IndustryCell({ active, commonOverride, industryCode, entityType, entityId, testId }) {
  const effectiveActive = active || commonOverride;
  return (
    <td
      data-cell={commonOverride ? undefined : 'industry'}
      data-industry-code={commonOverride ? undefined : industryCode}
      data-entity-type={commonOverride ? undefined : entityType}
      data-entity-id={commonOverride ? undefined : entityId}
      data-testid={testId}
      style={{
        textAlign: 'center', padding: 0, cursor: commonOverride ? 'not-allowed' : 'pointer',
        background: commonOverride ? 'rgba(14,165,233,0.08)' : (active ? 'rgba(59,130,246,0.12)' : 'var(--surface)'),
        borderRight: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9',
        opacity: commonOverride ? 0.85 : 1
      }}
      title={commonOverride ? 'Locked — entity is marked Common (available to all industries).' : (active ? 'Mapped — click to unmap' : 'Click to map')}
    >
      <div style={{
        width: 18, height: 18, margin: '6px auto', borderRadius: 3,
        background: effectiveActive ? (commonOverride ? '#0ea5e9' : '#3b82f6') : 'var(--surface)',
        border: `1px solid ${effectiveActive ? (commonOverride ? '#0369a1' : '#1d4ed8') : '#cbd5e1'}`
      }} />
    </td>
  );
});
